import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { COLORS, FONTS } from '../../theme/tokens';
import { PAGE_SECTION_SX, PANEL_SX } from '../../theme/v2Styles';

export const ACCOUNT_NAV_ITEMS = [
  { labelKey: 'account.overview', to: '/dashboard' },
  { labelKey: 'account.orderHistory', to: '/my-orders' },
  { labelKey: 'account.wishlist', to: '/wishlist' },
  { labelKey: 'account.accountDetails', to: '/profile' },
];

const AccountPageShell = ({ title, children, maxWidth = 'lg' }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <Container maxWidth={maxWidth} sx={PAGE_SECTION_SX}>
      <Typography
        component="h1"
        sx={{
          fontFamily: FONTS.heading,
          fontWeight: 600,
          fontSize: { xs: '1.625rem', md: '2rem' },
          color: COLORS.textPrimary,
          mb: { xs: 2.5, md: 4 },
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '240px minmax(0, 1fr)' },
          gap: { xs: 3, md: 5 },
          alignItems: 'start',
        }}
      >
        <Box
          component="nav"
          aria-label={t('account.myAccount')}
          sx={{
            ...PANEL_SX,
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            overflowX: { xs: 'auto', md: 'visible' },
            p: { xs: 1, md: '8px 0' },
            gap: { xs: 1, md: 0 },
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {ACCOUNT_NAV_ITEMS.map((item) => {
            const active = pathname === item.to;
            return (
              <Box
                key={item.to}
                component={RouterLink}
                to={item.to}
                aria-current={active ? 'page' : undefined}
                sx={{
                  flexShrink: 0,
                  px: { xs: 2, md: '22px' },
                  py: { xs: 1, md: '13px' },
                  borderRadius: { xs: '999px', md: 0 },
                  border: { xs: `1px solid ${active ? COLORS.green : COLORS.borderControl}`, md: 0 },
                  borderLeft: { md: `3px solid ${active ? COLORS.gold : 'transparent'}` },
                  bgcolor: active ? 'rgba(58,79,65,0.06)' : 'transparent',
                  color: active ? COLORS.green : COLORS.textBody,
                  fontSize: '0.875rem',
                  fontWeight: active ? 700 : 400,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { color: COLORS.green, bgcolor: 'rgba(58,79,65,0.04)' },
                }}
              >
                {t(item.labelKey)}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ minWidth: 0 }}>{children}</Box>
      </Box>
    </Container>
  );
};

export default AccountPageShell;
